import KeyValuePair from '@/modules/core/support/KeyValuePair';
import CalendarEventType from '@/modules/calendar/model/CalendarEventType';
import moment from 'moment'

/**
 * Define los tipos de repetición del evento
 */
export enum EventRepeat {
    Weekly = 'weekly',
    Monthly = 'monthly',
    Annual = 'annual',
    Once = 'once',
}

/**
 * Días de la semana, para el evento con repetición semanal
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export enum WeekDays {
    None = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
    Sunday = 7,
}

export enum Months {
    None = 0,
    January,
    February,
    March,
    April,
    May,
    June,
    July,
    August,
    September,
    October,
    November,
    December,
}

/**
 * Evento de calendario
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export default class CalendarEvent {
    /**
     * Repetición del evento
     *
     * Indica la asiduidad con la que ocurre un evento en el calendario.
     */
    public repeat = EventRepeat.Once

    /**
     * Día de la semana
     *
     * Cuando la repetición del evento es semanal, esta propiedad define que día
     * de la semana ocurre el event, de Lunes a Viernes.
     */
    public weekDay = WeekDays.None

    /**
     * Fecha
     *
     * Cuando la repetición del evento es única, quiere decir que el evento solo
     * ocurre una vez en el calendario.
     *
     * Esta propiedad define el día, més y año en el que ocurre el evento
     */
    public date = new Date()

    /**
     * Día del mes
     *
     * Cuando la repetición del evento es mensual y anual esta propiedad define
     * que día del mes ocurre el evento
     */
    public monthDay = 0

    /**
     * Mes del año
     *
     * Cuando la repetición del evento es anual esta propiedad define que mes del
     * año ocurre el evento, funciona en conjunto con monthDay.
     */
    public month = Months.None

    public name = ''

    /**
     * Indica si el día que coincida con el evento es un día no laborable
     */
    private _nonworking: boolean | null = null

    /**
     * Indica que el evento del calendario es festivo o no.
     */
    public festive: boolean = false

    /**
     * Tipo de evento de calendario
     */
    public type = new CalendarEventType()

    /**
     * Constructor
     *
     * @param id
     */
    constructor (public readonly id = 0) {

    }

    /**
     * Indica si el evento se repite con frecuencia semanal. Es decir, ocurre
     * una vez cada semana.
     */
    public get isWeekly () {
        return this.repeat === EventRepeat.Weekly
    }

    /**
     * Indica si el evento se repite de forma mensual. Es decir, ocurre una vez
     * cada mes
     */
    public get isMonthly () {
        return this.repeat === EventRepeat.Monthly
    }

    /**
     * Indica si el evento se repite de forma anual, es decir, una vez al año
     */
    public get isAnnual () {
        return this.repeat === EventRepeat.Annual
    }

    /**
     * Indica si el evento solo ocurre una vez en el calendario
     */
    public get isJustOnce (): boolean {
        return this.repeat === EventRepeat.Once
    }

    /**
     * Indica si la entidad es nueva y todavía no ha sido insertada en el servidor
     */
    public get isNew () {
        return !this.id
    }

    /**
     * Indica si el día que coincide con el evento es un día no laborable
     */
    public get isNonworking (): boolean {
        return this._nonworking === null ? this.type.nonworking : this._nonworking
    }

    /**
     * Establece el parámetro que indica si los días que coincidan con el evento
     * son días no laborables
     *
     * @param nonworking
     */
    public set isNonworking (nonworking: boolean) {
        this._nonworking = nonworking
    }

    /**
     * Resetea el parámetro de día no laborable
     */
    public resetNonworking () {
        this._nonworking = null

        return this
    }

    /**
     * Comprueba si el evento tiene lugar en la fecha argumentada
     *
     * @param date
     */
    public match (date: Date): boolean {
        const lastDay = (new Date(date.getFullYear(), date.getMonth() + 1, 0)).getDate()
        switch (this.repeat) {
            // Si el evento es semanal, comprueba que el día de la semana de la fecha
            // introducida coincida
            case EventRepeat.Weekly:
                const weekDay = date.getDay() === 0 ? 7 : date.getDay()

                return weekDay === this.weekDay
            // Si el evento es mensual, comprueba que el día del mes del evento coincida
            // con el día del mes de al fecha
            case EventRepeat.Monthly:
                if (this.isLastMonthDay) {
                    return lastDay === date.getDate()
                }

                return this.monthDay === date.getDate()
            // Si el evento es anual comprueba que día y mes de la fecha coincida con la
            // fecha argumentada
            case EventRepeat.Annual:
                // El evento esta configurado para que ocurra el último día de cada mes
                if (this.isLastMonthDay) {
                    return lastDay === date.getDate() && (date.getMonth() + 1) === this.month
                }

                return (date.getMonth() + 1) === this.month
                    && date.getDate() === this.monthDay
            // Si el evento es único comprueba que la fecha introducida sea la misma que la fecha del evento
            case EventRepeat.Once:
                return this.formatDate(this.date) === this.formatDate(date)
            default:
                return false
        }
    }

    /**
     * Comprueba si el evento esta configurado para ocurrir el último día del mes
     */
    public get isLastMonthDay (): boolean {
        return (this.isMonthly || this.isAnnual) && this.monthDay === 31
    }

    /**
     * Formatea una fecha al formato DD-MM-YYY
     *
     * @param date
     */
    private formatDate (date: Date): string {
        return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`
    }

    public static deserialize (data: KeyValuePair) {
        const event = new this(data.id)
        event.repeat = data.repeat
        event.weekDay = data.week_day
        event.monthDay = data.month_day
        event.month = data.month
        event.name = data.name
        event.type = CalendarEventType.deserialize(data.type)
        event._nonworking = data.nonworking
        event.festive = data.festive
        if (data.date) {
            event.date = moment(data.date, 'DD-MM-YYYY').toDate()
        }

        return event
    }

    /**
     * Convierte la entidad en un objeto plano para ser enviado al servidor
     */
    public serialize () {
        const data: KeyValuePair = {
            repeat: this.repeat.toString(),
            week_day: this.weekDay,
            month_day: this.monthDay,
            month: this.month,
            name: this.name,
            type: this.type.id,
            nonworking: this.isNonworking,
            festive: this.festive
        }

        if (this.isJustOnce && this.date) {
            data.date = moment(this.date).format('DD-MM-YYYY')
        }

        return data
    }
}
